import { useEffect, useState } from "react";
import { Badge, Button, Dropdown, Empty, List, Typography } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "../helpers/api";
import type { Notification, Paginated } from "../api-models";
import { DT_FORMATS } from "../helpers/constants";
import { notificationTarget } from "../helpers/docRoutes";
import { hasAnyGrant } from "../helpers/permissions";
import { useUser } from "../context/UserContext";

export function NotificationBell() {
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useUser();

  async function load() {
    const res = await api.get<Paginated<Notification>>("/notifications", { params: { pageSize: 10 } });
    setItems(res.data.data);
    setUnread(res.data.data.filter((n) => !n.readAt).length);
  }

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, []);

  async function markAllRead() {
    await api.post("/notifications/read", { ids: [] });
    load();
  }

  /** A notification is a pointer to the document that raised it — opening it reads it too. */
  async function openNotification(n: Notification, path: string) {
    setOpen(false);
    navigate(path);
    if (!n.readAt) {
      await api.post("/notifications/read", { ids: [n.id] });
      load();
    }
  }

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={["click"]}
      popupRender={() => (
        <div style={{ background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", borderRadius: 8, width: 340, maxHeight: 420, overflow: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px" }}>
            <Typography.Text strong>Notifications</Typography.Text>
            <Button type="link" size="small" onClick={markAllRead}>
              Mark all read
            </Button>
          </div>
          <List
            dataSource={items}
            locale={{ emptyText: <Empty description="No notifications" /> }}
            renderItem={(n) => {
              // Unreadable referents stay plain text rather than linking into a 403.
              const target = notificationTarget(n);
              const path = target && hasAnyGrant(user, target.permission) ? target.path : null;
              return (
                <List.Item
                  onClick={path ? () => openNotification(n, path) : undefined}
                  style={{ padding: "8px 12px", background: n.readAt ? undefined : "#f0f7ff", cursor: path ? "pointer" : undefined }}
                >
                  <div>
                    <div>{n.message}</div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(n.createdAt).format(DT_FORMATS.dateTime)}
                    </Typography.Text>
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      )}
    >
      <Badge count={unread} size="small">
        <Button type="text" icon={<BellOutlined style={{ fontSize: 18 }} />} />
      </Badge>
    </Dropdown>
  );
}
